// 1:1 port from age-of-mcp v3 (noticeboard.ts). The flattened part literals are
// reconstructed from the original helper functions; geometry is parity-gated
// identical.
//
// v3 noticeboard — AN OVERSIZED PARCHMENT, TILTED AT THE SKY. A big light
// parchment sheet (CANVAS) on a dark TIMBER frame, leaned BACK so its face tips
// up toward the camera, with three pinned notes (red ROOF_TRADE pins) and a
// small terracotta gable roof keeping the rain off. The board faces +z. 1x1.

import { defineModel, styleV3, type Part } from "../../lib";

const { CANVAS, PLASTER, ROOF_HOME, ROOF_TRADE, TIMBER, box, cyl, gable, poles, slab } = styleV3;

const NOTE_BLUE = "#cfdce8";

// The board leans back by TILT so its +z face tips upward.
const TILT = -0.32;
const COS = Math.cos(TILT), SIN = Math.sin(-TILT);   // SIN > 0: face tips up
const BOARD_Y = 0.46;                                // board centre height

// A box lying ON the tilted board plane: local (dx, dy) across the board
// face, `out` along its normal — composed into world position + the tilt.
function onBoard(
  w: number, h: number, dx: number, dy: number, out: number, color: string,
): Part {
  return box(w, h, 0.016, color, [
    dx,
    BOARD_Y + dy * COS + out * SIN,
    dy * SIN + out * COS,
  ], [TILT, 0, 0]);
}

function posts(): Part[] {
  return [-0.2, 0.2].map(x => cyl(0.032, 0.038, 0.5, 5, TIMBER, [x, 0.31, -0.04]));
}

// The dark frame and the oversized parchment proud of it.
function board(): Part[] {
  return [
    onBoard(0.6, 0.44, 0, 0, 0, TIMBER),
    onBoard(0.52, 0.36, 0, 0, 0.014, CANVAS),
  ];
}

// Pinned notes, slightly askew, each with a red pin head.
function notes(): Part[] {
  return [
    { ...onBoard(0.13, 0.15, -0.13, 0.04, 0.026, PLASTER), rotation: [TILT, 0, 0.1] },
    { ...onBoard(0.12, 0.12, 0.11, -0.02, 0.026, NOTE_BLUE), rotation: [TILT, 0, -0.12] },
    { ...onBoard(0.1, 0.1, 0.01, -0.09, 0.026, PLASTER), rotation: [TILT, 0, 0.06] },
    onBoard(0.026, 0.026, -0.13, 0.12, 0.034, ROOF_TRADE),
    onBoard(0.026, 0.026, 0.11, 0.05, 0.034, ROOF_TRADE),
  ];
}

// The little terracotta gable over the board, ridge running along x.
function roof(): Part {
  return gable(0.3, 0.68, 0.13, ROOF_HOME, 0, 0.66, 0.04, "x");
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.42, 0.42, undefined, 0.06)];
  if (s === 1) return [slab(0.42, 0.42, undefined, 0.06), ...posts()];
  if (s === 2) return [slab(0.42, 0.42, undefined, 0.06), ...posts(), ...board(), ...poles(0.5, 0.34, 0.6)];
  return [
    slab(0.42, 0.42, undefined, 0.06),
    ...posts(),
    ...board(),
    ...notes(),
    roof(),
  ];
}

export const noticeboardV3 = defineModel({
  id: "noticeboard-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Noticeboard (v3)",
  tags: ["building","noticeboard","low-poly","age-of-mcp","v3"],
  summary: "Low-poly noticeboard.",
  description: "A posting board where couriers leave written reports to be read.",
  author: "age-of-mcp",
  footprint: 0.3,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
